import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Mic, Loader2 } from "lucide-react";
import { api } from "@/lib/http";
import { useT } from "@/i18n/I18nContext";
import { useLoadout } from "@/context/LoadoutContext";
import { parseVoiceFood, parseVoiceFoodList } from "@/lib/parseVoiceFood";
import { VoicePrivacyHint } from "@/components/VoicePrivacyHint";

// Subtle haptic + waveform feedback when recording begins.
// iOS Safari still doesn't implement navigator.vibrate — we call it defensively
// so Android + PWA users get the buzz; Safari silently ignores the call.
const buzz = (pattern = [12, 24, 12]) => {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
  } catch { /* noop */ }
};

// Voice-driven quick food search.
// Tap → record ~4s → OpenAI Whisper transcription → parse query + quantity →
// invoke onResult({query, quantity_g, pieces}) so the parent can open the
// FoodSearchDialog with all fields pre-filled.
//
// Reuses the existing /client/ai-coach/transcribe endpoint (Whisper, 30 req/h).
const VoiceFoodButton = forwardRef(function VoiceFoodButton({ onResult, onQuery, className = "" }, ref) {
  const { t, lang } = useT();
  const { palette } = useLoadout();
  const [state, setState] = useState("idle"); // idle | recording | processing
  // 5 normalized amplitude values (0..1) driven by the AnalyserNode below.
  // The overlay renders bar heights from this array; when empty it falls back
  // to a synthesized pulse (e.g. Safari before mic permission is granted, or
  // browsers that don't expose AudioContext).
  const [levels, setLevels] = useState([0, 0, 0, 0, 0]);
  const mediaRecRef = useRef(null);
  const streamRef = useRef(null);
  const stopTimerRef = useRef(null);
  const btnRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);

  const stopAmplitudeLoop = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    try { analyserRef.current?.disconnect?.(); } catch { /* noop */ }
    analyserRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* noop */ }
      audioCtxRef.current = null;
    }
    setLevels([0, 0, 0, 0, 0]);
  };

  const startAmplitudeLoop = (stream) => {
    // Web Audio graph: MediaStream → AnalyserNode → nothing (we only read).
    // Explicitly guard against browsers that don't expose an AudioContext,
    // and against the resume() promise being rejected on iOS Safari when
    // the user gesture chain has already expired.
    const AudioCtx = typeof window !== "undefined"
      ? (window.AudioContext || window.webkitAudioContext)
      : null;
    if (!AudioCtx) return;
    let ctx;
    try {
      ctx = new AudioCtx();
    } catch { return; }
    audioCtxRef.current = ctx;
    // Safari sometimes starts the context suspended even inside a user
    // gesture; resume() is a no-op on Chrome/Firefox.
    try { ctx.resume?.(); } catch { /* noop */ }
    let source;
    try {
      source = ctx.createMediaStreamSource(stream);
    } catch { stopAmplitudeLoop(); return; }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;              // 64 frequency bins → plenty for 5 bars
    analyser.smoothingTimeConstant = 0.7; // smooth over ~7 frames → bars glide
    source.connect(analyser);
    analyserRef.current = analyser;
    const buf = new Uint8Array(analyser.frequencyBinCount);

    // Split 64 bins into 5 log-spaced buckets that roughly track speech
    // energy (low-mid dominant). Bar 0 = 40-150 Hz, Bar 4 = 3-6 kHz.
    const buckets = [
      [1, 4],    // low
      [4, 10],   // low-mid
      [10, 22],  // mid
      [22, 40],  // mid-high
      [40, 62],  // high
    ];

    const NOISE_FLOOR = 12;   // 0..255 — drop faint constant hiss
    const GAIN = 1.35;

    const tick = () => {
      analyser.getByteFrequencyData(buf);
      const next = buckets.map(([a, b]) => {
        let sum = 0;
        for (let i = a; i < b; i++) sum += Math.max(0, buf[i] - NOISE_FLOOR);
        const avg = sum / (b - a);
        return Math.min(1, (avg / (255 - NOISE_FLOOR)) * GAIN);
      });
      setLevels(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
  };

  const cleanup = () => {
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
    stopAmplitudeLoop();
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    mediaRecRef.current = null;
  };

  const beginRecording = async () => {
    if (state !== "idle") return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      toast.error(t("voiceFood.notSupported"));
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error(t("voiceFood.micDenied"));
      return;
    }
    streamRef.current = stream;
    // Confirm the mic actually opened with a soft haptic pattern. Users driving
    // recording from the hidden QuickTrackMenu "Stimme" entry can't see the
    // button pulse, so this is their only feedback that recording started.
    buzz([12, 30, 12]);
    // Real-time amplitude → 5 waveform bars via the AnalyserNode graph.
    // Fails silently on browsers without AudioContext (overlay falls back to
    // the synthesized pulse animation).
    startAmplitudeLoop(stream);
    const mimes = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
    const mime = mimes.find((m) => MediaRecorder.isTypeSupported(m)) || "";
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    mediaRecRef.current = rec;
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = async () => {
      const blob = new Blob(chunks, { type: mime || "audio/webm" });
      cleanup();
      if (blob.size < 500) {
        setState("idle");
        toast.error(t("voiceFood.tooShort"));
        return;
      }
      setState("processing");
      try {
        const b64 = await blobToBase64(blob);
        const useLang = ["it", "en", "de"].includes(lang) ? lang : undefined;
        const { data } = await api.post("/client/ai-coach/transcribe", {
          audio_base64: b64,
          lang: useLang,
        });
        const text = (data?.text || "").trim();
        if (!text) {
          toast.error(t("voiceFood.notRecognized"));
        } else {
          const { items } = parseVoiceFoodList(text);
          if (!items.length) {
            toast.error(t("voiceFood.notRecognized"));
          } else if (onResult) {
            // Pass first item's fields (backward compat) + full items array so
            // the parent can decide whether to open a single-food dialog or the
            // multi-food sheet.
            onResult({ ...items[0], items });
          } else if (onQuery) {
            onQuery(items[0].query);
          }
        }
      } catch (err) {
        toast.error(err?.response?.data?.detail || t("voiceFood.error"));
      } finally {
        setState("idle");
      }
    };
    rec.start();
    setState("recording");
    // Auto-stop after 4s so users don't have to release
    stopTimerRef.current = setTimeout(() => { try { rec.stop(); } catch {} }, 4000);
  };

  const stopEarly = () => {
    // Short haptic tick so users feel "stop" registered on iPhone (Android only —
    // Safari ignores vibrate silently).
    buzz(10);
    try { mediaRecRef.current?.stop(); } catch {}
  };

  const disabled = state === "processing";
  const label = state === "recording" ? t("voiceFood.listening")
              : state === "processing" ? t("voiceFood.processing")
              : t("voiceFood.tap");

  // Expose imperative handles so parents (e.g. QuickTrackMenu → Stimme) can
  // trigger recording synchronously from within their OWN user-gesture
  // handler. Calling beginRecording() directly preserves the transient
  // activation iOS Safari requires for `getUserMedia`, unlike a delegated
  // `.click()` on a different DOM node which some browsers strip.
  useImperativeHandle(ref, () => ({
    // eslint-disable-next-line react-hooks/exhaustive-deps
    beginRecording,
    stop: stopEarly,
    click: () => btnRef.current?.click(),
    focus: () => btnRef.current?.focus(),
  }));

  return (
    <>
    <button
      ref={btnRef}
      type="button"
      onClick={state === "recording" ? stopEarly : beginRecording}
      disabled={disabled}
      data-testid="voice-food-btn"
      aria-label={label}
      title={label}
      className={`group inline-flex items-center gap-2 rounded-full pl-3 pr-4 py-1.5 text-[13px] font-semibold shadow-sm active:scale-[0.97] transition-all duration-200 ${
        state === "recording"
          ? "bg-rose-500 text-white shadow-rose-500/30"
          : disabled
          ? "bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400"
          : "text-white hover:shadow-md"
      } ${className}`}
      style={
        state === "recording" || disabled
          ? undefined
          : {
              backgroundImage: `linear-gradient(to bottom, color-mix(in srgb, ${palette.via} 62%, #1F2E33), color-mix(in srgb, ${palette.from} 62%, #1F2E33))`,
              boxShadow: `0 4px 12px -2px ${palette.accent}55`,
            }
      }
    >
      <span className={`relative flex h-6 w-6 items-center justify-center rounded-full ${
        state === "recording" ? "bg-white/25" : "bg-white/20 group-hover:bg-white/30"
      } transition`}>
        {state === "recording" && (
          <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" aria-hidden />
        )}
        {state === "processing" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
      </span>
      <span className="tracking-tight">{label}</span>
    </button>
    {/* Floating recording overlay — visible even when the button itself is
        rendered inside `sr-only` (Dashboard mounts a hidden instance so the
        QuickTrackMenu "Stimme" entry can drive it imperatively). Users need
        SOMETHING on-screen to confirm the mic is actually listening. */}
    {(state === "recording" || state === "processing") && (
      <VoiceRecordingOverlay
        state={state}
        label={label}
        accent={palette.accent}
        levels={levels}
        onStop={stopEarly}
        stopLabel={t("voiceFood.tapToStop") || t("common.stop") || "Stop"}
      />
    )}
    </>
  );
});

export default VoiceFoodButton;

// ---------------------------------------------------------------------------
// Floating overlay shown while the mic is active. Anchors bottom-center on
// mobile, with 5 pulsing "waveform" bars synced to a low-frequency animation.
// Tap-to-stop lets users end the recording sooner than the 4-second auto-cap.
// Rendered in a portal so it's never clipped by parent overflow rules
// (e.g. the hidden `sr-only` wrapper the Dashboard uses).
// ---------------------------------------------------------------------------
function VoiceRecordingOverlay({ state, label, accent = "#EF4444", levels, onStop, stopLabel }) {
  const bars = 5;
  const isRecording = state === "recording";
  // Live-amplitude mode when the AnalyserNode is feeding us non-zero data.
  // We treat "all zeros for the whole frame" as "no signal yet" and fall
  // back to the synthesized pulse — otherwise the bars would sit flat as
  // soon as the user is silent, which looks like the mic died.
  const hasLive = Array.isArray(levels) && levels.length >= bars
    && levels.some((v) => v > 0.02);

  // Colour ramp: cool teal at silence → warm amber at peak. Both endpoints
  // are pastel enough to stay readable against the rose-500 pill.
  // v=0 → #A8E5DE (cool teal), v=1 → #FFA657 (warm amber).
  // We use a gentle sqrt curve so quiet voices already shift a bit warmer
  // instead of sitting stubbornly teal until the user shouts.
  const barColor = (v) => {
    const k = Math.sqrt(Math.max(0, Math.min(1, v)));
    const r = Math.round(168 + (255 - 168) * k);   // 168 → 255
    const g = Math.round(229 + (166 - 229) * k);   // 229 → 166
    const b = Math.round(222 + ( 87 - 222) * k);   // 222 →  87
    return `rgb(${r}, ${g}, ${b})`;
  };
  return createPortal(
    <div
      className="fixed inset-x-0 z-[120] flex flex-col items-center justify-end gap-2 pointer-events-none"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)" }}
      role="status"
      aria-live="polite"
      data-testid="voice-recording-overlay"
    >
      <span className="pointer-events-auto">
        <VoicePrivacyHint tone="dark" testId="voice-privacy-overlay" className="bg-slate-800/90 backdrop-blur shadow" />
      </span>
      <button
        type="button"
        onClick={isRecording ? onStop : undefined}
        disabled={!isRecording}
        aria-label={isRecording ? stopLabel : label}
        className={`pointer-events-auto flex items-center gap-3 rounded-full px-5 py-3 shadow-2xl backdrop-blur transition-all duration-200 ${
          isRecording ? "bg-rose-500/95 text-white cursor-pointer active:scale-[0.97]"
                      : "bg-slate-800/90 text-white"
        }`}
        style={isRecording ? { boxShadow: `0 8px 32px -6px ${accent}66` } : undefined}
        data-testid="voice-recording-overlay-btn"
      >
        {/* Waveform bars — real-time mic amplitude when the AnalyserNode
            graph is running, synthesized pulse otherwise. Height range
            6..26px keeps the pill compact but readable. */}
        <span className="flex items-end gap-[3px] h-6" aria-hidden data-testid="voice-recording-overlay-bars">
          {Array.from({ length: bars }).map((_, i) => {
            if (hasLive) {
              const v = Math.max(0, Math.min(1, levels[i] || 0));
              const h = 6 + Math.round(v * 20);   // 6px..26px
              const op = 0.55 + v * 0.45;         // 0.55..1.0
              return (
                <span
                  key={i}
                  className="w-[3px] rounded-full"
                  style={{
                    height: `${h}px`,
                    opacity: op,
                    backgroundColor: barColor(v),
                    // 90ms transition keeps the motion smooth even when the
                    // requestAnimationFrame source is jittery (60→30 fps),
                    // and lets the colour ramp glide instead of snap.
                    transition: "height 90ms linear, opacity 90ms linear, background-color 120ms linear",
                  }}
                />
              );
            }
            return (
              <span
                key={i}
                className="w-[3px] rounded-full bg-white/90"
                style={{
                  animation: isRecording
                    ? `voiceBarPulse 900ms ease-in-out ${i * 90}ms infinite`
                    : "none",
                  height: isRecording ? undefined : "12px",
                }}
              />
            );
          })}
        </span>
        <span className="text-sm font-semibold tracking-tight">{label}</span>
        {isRecording && (
          <span className="text-[11px] uppercase tracking-wider opacity-80 pl-1 border-l border-white/30 ml-1">
            {stopLabel}
          </span>
        )}
      </button>
      {/* Local keyframes so we don't have to touch Tailwind config */}
      <style>{`
        @keyframes voiceBarPulse {
          0%, 100% { height: 6px;  opacity: 0.55; }
          25%      { height: 22px; opacity: 1;    }
          50%      { height: 10px; opacity: 0.75; }
          75%      { height: 18px; opacity: 0.95; }
        }
      `}</style>
    </div>,
    typeof document !== "undefined" ? document.body : null,
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}
